import React from 'react';
import './App.css';
import rentegicLogo from './images/rentegic-logo.png';
import { ContactList, ContactForm, useContacts, ContactWithDetails, ContactSaveResult } from './components/contacts';
import { PropertyList, PropertyForm, useProperties, PropertyWithDetails } from './components/properties';
import { LeaseList, LeaseForm, useLeases, LeaseWithDetails, LeaseListItem, LeaseSearchParams } from './components/leases';
import { ExpenseList, ExpenseForm, useExpenses } from './components/expenses';
import { NoteList, NoteForm, useNotes, NoteWithDetails, NoteSearchParams } from './components/notes';
import { AddressList, AddressForm, useAddresses, Address } from './components/addresses';
import { CategoryManagement } from './components/category';
import { ReportsList, ReportsPanel, useReports } from './components/reports';
import { ImportsList, ImportsPanel, useImports } from './components/imports';
import { ExpenseWithDetails, ExpenseSearchParams } from './components/expenses';
import CommunicationsApp, { OAuthReturn } from './components/communications/CommunicationsApp';
import AppNavTab from './AppNavTab';
import AppLogoutButton from './AppLogoutButton';
import { useAuth } from '../components/auth';

type ActiveView = 'contacts' | 'properties' | 'leases' | 'expenses' | 'notes' | 'addresses' | 'reports' | 'imports' | 'categories' | 'communications';

const ViewPanel: React.FC<{
  view: ActiveView;
  activeView: ActiveView;
  children: React.ReactNode;
}> = ({ view, activeView, children }) => (
  <div className={`app-view${activeView === view ? ' app-view-active' : ''}`}>
    {children}
  </div>
);

// Main app content component - only rendered when authenticated
const AppContent: React.FC<{ user: any; logout: () => Promise<void> }> = ({ user, logout }) => {
  const {
    contacts,
    error,
    pagination,
    updateSearchParams,
    deleteContact,
    getContactById,
    refetch,
    globalLoading,
    loadingMessage
  } = useContacts();

  const {
    properties,
    loading: propertiesLoading,
    error: propertiesError,
    pagination: propertiesPagination,
    updateSearchParams: updatePropertiesSearchParams,
    deleteProperty,
    refetch: refetchProperties,
    globalLoading: propertiesGlobalLoading,
    loadingMessage: propertiesLoadingMessage
  } = useProperties();

  const {
    leases,
    loading: leasesLoading,
    error: leasesError,
    pagination: leasesPagination,
    updateSearchParams: updateLeasesSearchParams,
    createLease,
    updateLease,
    endLease,
    deleteLease,
    getLeaseById,
    globalLoading: leasesGlobalLoading,
    loadingMessage: leasesLoadingMessage
  } = useLeases();

  const {
    expenses,
    loading: expensesLoading,
    error: expensesError,
    pagination: expensesPagination,
    updateSearchParams: updateExpensesSearchParams,
    deleteExpense,
    getExpenseById,
    globalLoading: expensesGlobalLoading,
    loadingMessage: expensesLoadingMessage
  } = useExpenses();

  const {
    notes,
    error: notesError,
    pagination: notesPagination,
    updateSearchParams: updateNotesSearchParams,
    deleteNote,
    getNoteById,
    globalLoading: notesGlobalLoading,
    loadingMessage: notesLoadingMessage
  } = useNotes();

  const {
    addresses,
    loading: addressesLoading,
    error: addressesError,
    pagination: addressesPagination,
    updateSearchParams: updateAddressesSearchParams,
    deleteAddress,
    refetch: refetchAddresses,
    globalLoading: addressesGlobalLoading,
    loadingMessage: addressesLoadingMessage
  } = useAddresses();

  const {
    reportTypes,
    reports,
    selectedType,
    setSelectedType,
    selectedReportId,
    setSelectedReportId,
    properties: reportProperties,
    years: reportYears,
    selectedPropertyId,
    setSelectedPropertyId,
    selectedYear,
    setSelectedYear,
    reportResult,
    error: reportsError,
    globalLoading: reportsGlobalLoading,
    loadingMessage: reportsLoadingMessage,
    loadYearsForProperty,
    runExpensesByPropertyReport,
    downloadHtml,
    clearReportResult
  } = useReports();

  const {
    sources: importSources,
    selectedSourceId,
    selectSource: selectImportSource,
    selectedFiles: importFiles,
    setFiles: setImportFiles,
    absenteeFile,
    propertyFile,
    setAbsenteeFile,
    setPropertyFile,
    importResult,
    error: importsError,
    globalLoading: importsGlobalLoading,
    loadingMessage: importsLoadingMessage,
    runCrsImport,
    runCrsAbsenteeMergeImport,
    clearImportResult
  } = useImports();

  const [selectedContact, setSelectedContact] = React.useState<ContactWithDetails | null>(null);
  const [selectedProperty, setSelectedProperty] = React.useState<PropertyWithDetails | null>(null);
  const [selectedLease, setSelectedLease] = React.useState<LeaseWithDetails | null>(null);
  const [selectedExpense, setSelectedExpense] = React.useState<ExpenseWithDetails | null>(null);
  const [selectedNote, setSelectedNote] = React.useState<NoteWithDetails | null>(null);
  const [selectedAddress, setSelectedAddress] = React.useState<Address | null>(null);
  const [creatingViews, setCreatingViews] = React.useState<Partial<Record<ActiveView, boolean>>>({});
  const [activeView, setActiveView] = React.useState<ActiveView>('contacts');
  const [dirtyViews, setDirtyViews] = React.useState<Partial<Record<ActiveView, boolean>>>({});
  const [oauthReturn, setOauthReturn] = React.useState<OAuthReturn | null>(null);

  const setViewCreating = React.useCallback((view: ActiveView, creating: boolean) => {
    setCreatingViews((prev) => {
      if (creating) {
        if (prev[view]) return prev;
        return { ...prev, [view]: true };
      }
      if (!prev[view]) return prev;
      const next = { ...prev };
      delete next[view];
      return next;
    });
  }, []);

  const handleViewDirtyChange = React.useCallback((view: ActiveView, dirty: boolean) => {
    setDirtyViews((prev) => {
      if (dirty) {
        if (prev[view]) return prev;
        return { ...prev, [view]: true };
      }
      if (!prev[view]) return prev;
      const next = { ...prev };
      delete next[view];
      return next;
    });
  }, []);

  const isViewFormOpen = (view: ActiveView, selected: unknown) =>
    Boolean(creatingViews[view] || selected);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    const oauth = params.get('oauth');
    if (!view && !oauth) return;

    if (view === 'communications') {
      setActiveView('communications');
    }
    if (oauth === 'success' || oauth === 'error') {
      setOauthReturn({
        status: oauth,
        channelId: params.get('channelId') ? parseInt(params.get('channelId')!, 10) : undefined,
        reason: params.get('reason') || undefined
      });
    }
    window.history.replaceState({}, '', '/');
  }, []);

  const handleSelectContact = (contact: ContactWithDetails) => {
    setSelectedContact(contact);
    setViewCreating('contacts', false);
  };

  const handleSelectProperty = (property: PropertyWithDetails) => {
    setSelectedProperty(property);
    setViewCreating('properties', false);
  };

  const handleSelectLease = async (lease: LeaseListItem) => {
    setViewCreating('leases', false);

    const fullLease = await getLeaseById(lease.id);
    if (fullLease) {
      setSelectedLease(fullLease);
    } else {
      setSelectedLease(null);
    }
  };

  const handleSelectExpense = async (expense: ExpenseWithDetails) => {
    setViewCreating('expenses', false);

    const fullExpense = await getExpenseById(expense.id);
    if (fullExpense) {
      setSelectedExpense(fullExpense);
    } else {
      setSelectedExpense(expense);
    }
  };

  const handleSelectNote = async (note: NoteWithDetails) => {
    setViewCreating('notes', false);

    const fullNote = await getNoteById(note.id);
    if (fullNote) {
      setSelectedNote(fullNote);
    } else {
      setSelectedNote(note);
    }
  };

  const handleSelectAddress = (address: Address) => {
    setSelectedAddress(address);
    setViewCreating('addresses', false);
  };

  const handleCreateNew = () => {
    switch (activeView) {
      case 'contacts':
        setSelectedContact(null);
        setViewCreating('contacts', true);
        break;
      case 'properties':
        setSelectedProperty(null);
        setViewCreating('properties', true);
        break;
      case 'leases':
        setSelectedLease(null);
        setViewCreating('leases', true);
        break;
      case 'expenses':
        setSelectedExpense(null);
        setViewCreating('expenses', true);
        break;
      case 'notes':
        setSelectedNote(null);
        setViewCreating('notes', true);
        break;
      case 'addresses':
        setSelectedAddress(null);
        setViewCreating('addresses', true);
        break;
      default:
        break;
    }
  };

  const handleViewChange = (view: ActiveView) => {
    setActiveView(view);
  };

  const handleReportPropertyChange = React.useCallback(async (propertyId: number | null) => {
    setSelectedPropertyId(propertyId);
    setSelectedYear(null);
    clearReportResult();

    if (propertyId) {
      await loadYearsForProperty(propertyId);
    }
  }, [clearReportResult, loadYearsForProperty, setSelectedPropertyId, setSelectedYear]);

  const handleGenerateReport = React.useCallback(async () => {
    if (!selectedPropertyId || !selectedYear) {
      return;
    }

    await runExpensesByPropertyReport(selectedPropertyId, selectedYear);
  }, [runExpensesByPropertyReport, selectedPropertyId, selectedYear]);

  const handleDownloadReportHtml = React.useCallback(async () => {
    if (!selectedPropertyId || !selectedYear) {
      return;
    }

    await downloadHtml(selectedPropertyId, selectedYear);
  }, [downloadHtml, selectedPropertyId, selectedYear]);

  const handleContactSaveSuccess = async (result: ContactSaveResult) => {
    setViewCreating('contacts', false);
    await refetch();

    if (result.staySelected) {
      const refreshed = await getContactById(result.contactId);
      setSelectedContact(refreshed);
      return;
    }

    setSelectedContact(null);
  };

  const handleSaveSuccess = () => {
    switch (activeView) {
      case 'contacts':
        // Contact save is handled by handleContactSaveSuccess
        break;
      case 'properties':
        setSelectedProperty(null);
        setViewCreating('properties', false);
        refetchProperties();
        break;
      case 'leases':
        setSelectedLease(null);
        setViewCreating('leases', false);
        updateLeasesSearchParams({});
        break;
      case 'expenses':
        setSelectedExpense(null);
        setViewCreating('expenses', false);
        updateExpensesSearchParams({});
        break;
      case 'notes':
        setSelectedNote(null);
        setViewCreating('notes', false);
        updateNotesSearchParams({});
        break;
      case 'addresses':
        setSelectedAddress(null);
        setViewCreating('addresses', false);
        refetchAddresses();
        break;
      default:
        break;
    }
  };

  const handleCancel = () => {
    switch (activeView) {
      case 'contacts':
        setSelectedContact(null);
        setViewCreating('contacts', false);
        break;
      case 'properties':
        setSelectedProperty(null);
        setViewCreating('properties', false);
        break;
      case 'leases':
        setSelectedLease(null);
        setViewCreating('leases', false);
        break;
      case 'expenses':
        setSelectedExpense(null);
        setViewCreating('expenses', false);
        break;
      case 'notes':
        setSelectedNote(null);
        setViewCreating('notes', false);
        break;
      case 'addresses':
        setSelectedAddress(null);
        setViewCreating('addresses', false);
        break;
      default:
        break;
    }
  };

  const handleDeleteContact = async (contact: ContactWithDetails) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${contact.firstName} ${contact.lastName}? This action cannot be undone.`
    );

    if (confirmed) {
      const success = await deleteContact(contact.id);
      if (success && selectedContact?.id === contact.id) {
        setSelectedContact(null);
        setViewCreating('contacts', false);
      }
    }
  };

  const handleDeleteProperty = async (property: PropertyWithDetails) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${property.name}? This action cannot be undone.`
    );

    if (confirmed) {
      const success = await deleteProperty(property.id);
      if (success && selectedProperty?.id === property.id) {
        setSelectedProperty(null);
        setViewCreating('properties', false);
      }
    }
  };

  const handleDeleteLease = async (lease: LeaseListItem) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the lease for ${lease.propertyName}? This action cannot be undone.`
    );

    if (confirmed) {
      try {
        await deleteLease(lease.id);
        if (selectedLease?.id === lease.id) {
          setSelectedLease(null);
          setViewCreating('leases', false);
        }
      } catch (error) {
        console.error('Error deleting lease:', error);
      }
    }
  };

  const handleLeaseSearch = React.useCallback((searchParams: LeaseSearchParams) => {
    updateLeasesSearchParams(searchParams);
  }, [updateLeasesSearchParams]);

  const handleDeleteExpense = async (expense: ExpenseWithDetails) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the expense for ${expense.vendorName}? This action cannot be undone.`
    );

    if (confirmed) {
      try {
        await deleteExpense(expense.id);
        if (selectedExpense?.id === expense.id) {
          setSelectedExpense(null);
          setViewCreating('expenses', false);
        }
      } catch (error) {
        console.error('Error deleting expense:', error);
      }
    }
  };

  const handleExpenseSearch = React.useCallback((searchParams: ExpenseSearchParams) => {
    updateExpensesSearchParams(searchParams);
  }, [updateExpensesSearchParams]);

  const handleDeleteNote = async (note: NoteWithDetails) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the note "${note.summary}"? This action cannot be undone.`
    );

    if (confirmed) {
      try {
        await deleteNote(note.id);
        if (selectedNote?.id === note.id) {
          setSelectedNote(null);
          setViewCreating('notes', false);
        }
      } catch (error) {
        console.error('Error deleting note:', error);
      }
    }
  };

  const handleDeleteAddress = async (address: Address) => {
    const addressStr = `${address.street1}, ${address.city}, ${address.state} ${address.zip}`;
    const confirmed = window.confirm(
      `Are you sure you want to delete ${addressStr}? This action cannot be undone.`
    );

    if (confirmed) {
      const success = await deleteAddress(address.id);
      if (success && selectedAddress?.id === address.id) {
        setSelectedAddress(null);
        setViewCreating('addresses', false);
      }
    }
  };

  const handleAddressSearch = React.useCallback((searchParams: any) => {
    updateAddressesSearchParams(searchParams);
  }, [updateAddressesSearchParams]);

  const handleNoteSearch = React.useCallback((searchParams: Partial<NoteSearchParams>) => {
    updateNotesSearchParams(searchParams);
  }, [updateNotesSearchParams]);

  return (
    <div className="crm-app app">
      <header className="app-header">
        <div className="app-header-bar">
          <img src={rentegicLogo} alt="Rentegic" className="app-header-logo" />
          <nav className="app-nav">
            <AppNavTab
              icon="properties"
              label="Properties"
              isActive={activeView === 'properties'}
              isDirty={Boolean(dirtyViews.properties)}
              onClick={() => handleViewChange('properties')}
            />
            <AppNavTab
              icon="leases"
              label="Leases"
              isActive={activeView === 'leases'}
              isDirty={Boolean(dirtyViews.leases)}
              onClick={() => handleViewChange('leases')}
            />
            <AppNavTab
              icon="contacts"
              label="Contacts"
              isActive={activeView === 'contacts'}
              isDirty={Boolean(dirtyViews.contacts)}
              onClick={() => handleViewChange('contacts')}
            />
            <AppNavTab
              icon="expenses"
              label="Expenses"
              isActive={activeView === 'expenses'}
              isDirty={Boolean(dirtyViews.expenses)}
              onClick={() => handleViewChange('expenses')}
            />
            <AppNavTab
              icon="communications"
              label="Communications"
              isActive={activeView === 'communications'}
              isDirty={Boolean(dirtyViews.communications)}
              onClick={() => handleViewChange('communications')}
            />
            <AppNavTab
              icon="notes"
              label="Notes"
              isActive={activeView === 'notes'}
              isDirty={Boolean(dirtyViews.notes)}
              onClick={() => handleViewChange('notes')}
            />
            <AppNavTab
              icon="reports"
              label="Reports"
              isActive={activeView === 'reports'}
              onClick={() => handleViewChange('reports')}
            />
            <AppNavTab
              icon="imports"
              label="Import"
              isActive={activeView === 'imports'}
              onClick={() => handleViewChange('imports')}
            />
            <AppNavTab
              icon="addresses"
              label="Addresses"
              isActive={activeView === 'addresses'}
              isDirty={Boolean(dirtyViews.addresses)}
              onClick={() => handleViewChange('addresses')}
            />
            <AppNavTab
              icon="categories"
              label="Categories"
              isActive={activeView === 'categories'}
              isDirty={Boolean(dirtyViews.categories)}
              onClick={() => handleViewChange('categories')}
            />
          </nav>
          <div className="app-header-actions">
            {user && (
              <AppLogoutButton email={user.email} onLogout={logout} />
            )}
          </div>
        </div>
      </header>

      <main className="app-main">
        <ViewPanel view="contacts" activeView={activeView}>
          <div className="app-sidebar">
            <div className="app-sidebar-section">
              <ContactList
                contacts={contacts}
                error={error}
                pagination={pagination}
                selectedContactId={selectedContact?.id}
                onSelectContact={handleSelectContact}
                onDeleteContact={handleDeleteContact}
                onPageChange={(page) => updateSearchParams({ page })}
                onLimitChange={(limit) => updateSearchParams({ limit, page: 1 })}
                onCreateNew={handleCreateNew}
                onSearch={updateSearchParams}
                onImportComplete={refetch}
                globalLoading={globalLoading}
                loadingMessage={loadingMessage}
              />
            </div>
          </div>

          <div className="app-content">
            {isViewFormOpen('contacts', selectedContact) ? (
              <ContactForm
                contact={selectedContact}
                filteredContacts={contacts}
                onSave={handleContactSaveSuccess}
                onCancel={handleCancel}
                onNavigate={handleSelectContact}
                onDirtyChange={(dirty) => handleViewDirtyChange('contacts', dirty)}
              />
            ) : (
              <div className="app-welcome-message">
                <h2>Welcome to Rentegic PM</h2>
                <p>Select a contact from the list to view and edit details, or create a new contact.</p>
              </div>
            )}
          </div>
        </ViewPanel>

        <ViewPanel view="properties" activeView={activeView}>
          <div className="app-sidebar">
            <div className="app-sidebar-section">
              <PropertyList
                properties={properties}
                loading={propertiesLoading}
                error={propertiesError}
                pagination={propertiesPagination}
                selectedPropertyId={selectedProperty?.id}
                onSelectProperty={handleSelectProperty}
                onDeleteProperty={handleDeleteProperty}
                onPageChange={(page) => updatePropertiesSearchParams({ page })}
                onLimitChange={(limit) => updatePropertiesSearchParams({ limit, page: 1 })}
                onCreateNew={handleCreateNew}
                onSearch={updatePropertiesSearchParams}
                globalLoading={propertiesGlobalLoading}
                loadingMessage={propertiesLoadingMessage}
              />
            </div>
          </div>

          <div className="app-content">
            {isViewFormOpen('properties', selectedProperty) ? (
              <PropertyForm
                property={selectedProperty}
                onSave={handleSaveSuccess}
                onCancel={handleCancel}
                onDirtyChange={(dirty) => handleViewDirtyChange('properties', dirty)}
              />
            ) : (
              <div className="app-welcome-message">
                <h2>Property Management</h2>
                <p>Select a property from the list to view and edit details, or create a new property.</p>
              </div>
            )}
          </div>
        </ViewPanel>

        <ViewPanel view="leases" activeView={activeView}>
          <div className="app-sidebar">
            <div className="app-sidebar-section">
              <LeaseList
                leases={leases}
                loading={leasesLoading}
                error={leasesError}
                pagination={leasesPagination}
                selectedLeaseId={selectedLease?.id}
                onSelectLease={handleSelectLease}
                onDeleteLease={handleDeleteLease}
                onSearch={handleLeaseSearch}
                onCreateNew={handleCreateNew}
                globalLoading={leasesGlobalLoading}
                loadingMessage={leasesLoadingMessage}
              />
            </div>
          </div>

          <div className="app-content">
            {isViewFormOpen('leases', selectedLease) ? (
              <LeaseForm
                lease={selectedLease}
                onSave={handleSaveSuccess}
                onCancel={handleCancel}
                onDirtyChange={(dirty) => handleViewDirtyChange('leases', dirty)}
                createLease={createLease}
                updateLease={updateLease}
                endLease={endLease}
                deleteLease={deleteLease}
              />
            ) : (
              <div className="app-welcome-message">
                <h2>Lease Management</h2>
                <p>Select a lease from the list to view and edit details, or create a new lease.</p>
              </div>
            )}
          </div>
        </ViewPanel>

        <ViewPanel view="expenses" activeView={activeView}>
          <div className="app-sidebar">
            <div className="app-sidebar-section">
              <ExpenseList
                expenses={expenses}
                loading={expensesLoading}
                error={expensesError}
                pagination={expensesPagination}
                selectedExpenseId={selectedExpense?.id}
                onSelectExpense={handleSelectExpense}
                onDeleteExpense={handleDeleteExpense}
                onSearch={handleExpenseSearch}
                onCreateNew={handleCreateNew}
                onImportComplete={() => updateExpensesSearchParams({})}
                globalLoading={expensesGlobalLoading}
                loadingMessage={expensesLoadingMessage}
              />
            </div>
          </div>

          <div className="app-content">
            {isViewFormOpen('expenses', selectedExpense) ? (
              <ExpenseForm
                expense={selectedExpense}
                onSave={handleSaveSuccess}
                onCancel={handleCancel}
                onDirtyChange={(dirty) => handleViewDirtyChange('expenses', dirty)}
              />
            ) : (
              <div className="app-welcome-message">
                <h2>Expense Management</h2>
                <p>Select an expense from the list to view and edit details, or create a new expense.</p>
              </div>
            )}
          </div>
        </ViewPanel>

        <ViewPanel view="notes" activeView={activeView}>
          <div className="app-sidebar">
            <div className="app-sidebar-section">
              <NoteList
                notes={notes}
                error={notesError}
                pagination={notesPagination}
                selectedNoteId={selectedNote?.id}
                onSelectNote={handleSelectNote}
                onDeleteNote={handleDeleteNote}
                onPageChange={(page) => updateNotesSearchParams({ page })}
                onLimitChange={(limit) => updateNotesSearchParams({ limit, page: 1 })}
                onCreateNew={handleCreateNew}
                onSearch={handleNoteSearch}
                globalLoading={notesGlobalLoading}
                loadingMessage={notesLoadingMessage}
              />
            </div>
          </div>

          <div className="app-content">
            {isViewFormOpen('notes', selectedNote) ? (
              <NoteForm
                note={selectedNote}
                onSave={handleSaveSuccess}
                onCancel={handleCancel}
                onDirtyChange={(dirty) => handleViewDirtyChange('notes', dirty)}
              />
            ) : (
              <div className="app-welcome-message">
                <h2>Notes Management</h2>
                <p>Select a note from the list to view and edit details, or create a new note.</p>
              </div>
            )}
          </div>
        </ViewPanel>

        <ViewPanel view="addresses" activeView={activeView}>
          <div className="app-sidebar">
            <div className="app-sidebar-section">
              <AddressList
                addresses={addresses}
                loading={addressesLoading}
                error={addressesError}
                pagination={addressesPagination}
                selectedAddressId={selectedAddress?.id}
                onSelectAddress={handleSelectAddress}
                onDeleteAddress={handleDeleteAddress}
                onPageChange={(page) => updateAddressesSearchParams({ page })}
                onLimitChange={(limit) => updateAddressesSearchParams({ limit, page: 1 })}
                onCreateNew={handleCreateNew}
                onSearch={handleAddressSearch}
                globalLoading={addressesGlobalLoading}
                loadingMessage={addressesLoadingMessage}
              />
            </div>
          </div>

          <div className="app-content">
            {isViewFormOpen('addresses', selectedAddress) ? (
              <AddressForm
                address={selectedAddress}
                onSave={handleSaveSuccess}
                onCancel={handleCancel}
                onDirtyChange={(dirty) => handleViewDirtyChange('addresses', dirty)}
              />
            ) : (
              <div className="app-welcome-message">
                <h2>Address Management</h2>
                <p>Select an address from the list to view and edit details, or create a new address.</p>
              </div>
            )}
          </div>
        </ViewPanel>

        <ViewPanel view="reports" activeView={activeView}>
          <div className="app-sidebar">
            <div className="app-sidebar-section">
              <ReportsList
                reportTypes={reportTypes}
                selectedType={selectedType}
                onTypeChange={setSelectedType}
                reports={reports}
                selectedReportId={selectedReportId}
                onSelectReport={setSelectedReportId}
              />
            </div>
          </div>
          <div className="app-content">
            <ReportsPanel
              properties={reportProperties}
              years={reportYears}
              selectedPropertyId={selectedPropertyId}
              onSelectProperty={handleReportPropertyChange}
              selectedYear={selectedYear}
              onSelectYear={setSelectedYear}
              onSubmit={handleGenerateReport}
              onDownloadHtml={handleDownloadReportHtml}
              reportResult={reportResult}
              error={reportsError}
              globalLoading={reportsGlobalLoading}
              loadingMessage={reportsLoadingMessage}
            />
          </div>
        </ViewPanel>

        <ViewPanel view="imports" activeView={activeView}>
          <div className="app-sidebar">
            <div className="app-sidebar-section">
              <ImportsList
                sources={importSources}
                selectedSourceId={selectedSourceId}
                onSelectSource={selectImportSource}
              />
            </div>
          </div>
          <div className="app-content">
            <ImportsPanel
              selectedSourceId={selectedSourceId}
              selectedFiles={importFiles}
              onSelectFiles={setImportFiles}
              onImport={runCrsImport}
              absenteeFile={absenteeFile}
              propertyFile={propertyFile}
              onSelectAbsentee={setAbsenteeFile}
              onSelectProperty={setPropertyFile}
              onAbsenteeMergeImport={runCrsAbsenteeMergeImport}
              importResult={importResult}
              onCloseResult={clearImportResult}
              error={importsError}
              globalLoading={importsGlobalLoading}
              loadingMessage={importsLoadingMessage}
            />
          </div>
        </ViewPanel>

        <ViewPanel view="communications" activeView={activeView}>
          <CommunicationsApp
            oauthReturn={oauthReturn}
            isActive={activeView === 'communications'}
            onDirtyChange={(dirty) => handleViewDirtyChange('communications', dirty)}
          />
        </ViewPanel>

        <ViewPanel view="categories" activeView={activeView}>
          <CategoryManagement
            onDirtyChange={(dirty) => handleViewDirtyChange('categories', dirty)}
          />
        </ViewPanel>
      </main>
    </div>
  );
};

// Main CRM App component - authentication is handled by AppRouter
const App: React.FC = () => {
  const { user, logout } = useAuth();

  // This component assumes user is already authenticated and has 'crm' role
  // Authentication and role checking is handled by AppRouter
  if (!user) {
    return null; // Should not happen, but handle gracefully
  }

  return <AppContent user={user} logout={logout} />;
};

export default App;
